import './Img.scss';
import cn from 'classnames';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Spinner } from './Spinner';

// Basically a ripoff of https://github.com/mbrevda/react-image/blob/master/src/index.js. They aren't updating to remove
// deprecated React functions, so I'm taking matters into my own hands.

interface ImgProps {
  src: string;
  unloader?: ReactNode;
  spinnerSize?: number;
  className?: string;
  alt?: string;
  backupSrcs?: string[];
  [passthrough: string]: any;
}

export const Img = ({
  src: primarySrc,
  unloader = <span />,
  spinnerSize = 20,
  className = '',
  alt = '',
  backupSrcs = [],
  ...props
}: ImgProps): ReactElement => {
  const [imgSrc, setImgSrc] = useState<string>();
  const [loadingState, setLoadingState] = useState('loading');

  useEffect(() => {
    if (primarySrc) {
      setImgSrc(undefined);

      setLoadingState('loading');
    }
  }, [primarySrc]);

  useEffect(() => {
    const srcsToTry = [primarySrc, ...backupSrcs];
    if (imgSrc === undefined || !srcsToTry.includes(imgSrc)) {
      // Only show the unloader once every src has failed, so it doesn't flash while loading.
      let failed = 0;
      srcsToTry.forEach(src => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
          setImgSrc(src);
          setLoadingState('loaded');
        };
        img.onerror = () => {
          failed += 1;
          if (failed === srcsToTry.length) {
            setLoadingState('error');
          }
        };
      });
    }
  }, [backupSrcs, imgSrc, primarySrc]);

  switch (loadingState) {
    case 'loaded':
      return <img {...props} alt={alt} src={imgSrc} className={className} />;
    case 'loading':
      if (props.width != null && props.height != null) {
        return (
          <div
            className={cn('img-placeholder', className)}
            style={{ width: props.width, height: props.height }}
          >
            <Spinner size={spinnerSize} />
          </div>
        );
      }
      return <Spinner size={spinnerSize} />;
    case 'error':
      return <>{unloader}</>;
    default:
      return <span />;
  }
};
